import React, { useEffect, useState } from "react";
import { Button, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { DetailsContainer, VehicleInfo, EventDetail } from "./styles";
import { useNavigate } from "react-router-dom";
import {useDealer} from "../../../context/dealer/dealerContext";

/**
 * VehicleDetails displays detailed information about a specific vehicle and its event history.
 * It fetches data based on the vehicleID from the URL and renders a card with vehicle information and a list of event history.
 * Each event displays a date and a description of the event that occurred.
 * @returns {React.ReactElement} A component with a card containing vehicle details and a list of event history.
 */
function VehicleDetails() {
  const { fetchVehicleDetails } = useDealer();
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { vehicleID } = useParams();
  const navigate = useNavigate();

  useEffect(() => {

    if (!vehicleID) {
      setError("No vehicle ID provided");
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const details = await fetchVehicleDetails({ vehicleID });
        setVehicleDetails(details);
        setError(""); // Reset error on successful fetch
      } catch (err) {
        console.error("Error fetching vehicle details:", err);
        setError(err.message || "Error fetching vehicle details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [vehicleID, fetchVehicleDetails]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!vehicleDetails) {
    return <Typography>No vehicle details found.</Typography>;
  }

  return (
    <DetailsContainer>
      <Card>
        <CardContent>
          <VehicleInfo>
            <div>
              <Typography variant="h5" gutterBottom>
                {vehicleDetails?.year} {vehicleDetails?.brand} {vehicleDetails.model}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                VIN: {vehicleDetails?.vin}
              </Typography>
            </div>
            <Button onClick={()=>navigate("/dealer/confirm-details")} variant="contained" color="secondary">
              {vehicleDetails?.status}
            </Button>
          </VehicleInfo>

          <Typography variant="h6" gutterBottom>
            Event History
          </Typography>

          {vehicleDetails.eventHistories.map((event, index) => (
            <EventDetail key={index}>
              <Typography variant="body1">
                {event.eventDate}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {event.eventDescription}
              </Typography>
            </EventDetail>
          ))}
        </CardContent>
      </Card>
    </DetailsContainer>
  );
}

export default VehicleDetails;
